/**
 * CreativeAbPage — manage A/B test variants for a campaign + watch the
 * per-variant conversion report update as assign/convert calls land.
 *
 * Flow:
 * - Enter a campaign_id → loads variants + report.
 * - Variant editor: add rows (variant_key, asset_kind, asset_ref, split_bps);
 * sum must equal 10_000 bps. Save PUTs the set atomically.
 * - Tester row: enter a click_id → Assign (picks variant deterministically
 * + writes a view) → Convert (flips converted=1).
 * - Live report table: variant_key, views, conversions, CR (bps → %).
 *
 * Brand: green only. Light theme. Zinc borders. 6-8px radius.
 */

import { useMemo, useState, type FormEvent } from "react";
import {
	useCreativeAbAssign,
	useCreativeAbConvert,
	useCreativeAbReplaceVariants,
	useCreativeAbReport,
	useCreativeAbVariants,
} from "./hooks";
import {
	TOTAL_BPS,
	type AssetKind,
	type AssignResponse,
	type VariantInput,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — green brand, zinc borders, 6-8px rounding.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const SELECT = `${INPUT} pr-8`;
const BTN_PRIMARY =
	"inline-flex items-center justify-center rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-green-700 hover:border-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"inline-flex items-center justify-center rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"inline-flex items-center justify-center rounded-[6px] border border-red-300 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";

const ASSET_KINDS: AssetKind[] = ["creative", "lp"];

function emptyDraft(): VariantInput {
	return {
 variant_key: "",
 asset_kind: "creative",
 asset_ref: "",
 split_bps: 0,
	};
}

function fmtBpsAsPct(bps: number): string {
	return `${(bps / 100).toFixed(2)}%`;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export interface CreativeAbPageProps {
	campaignId?: string;
}

export function CreativeAbPage(props: CreativeAbPageProps = {}) {
	const [campaignId, setCampaignId] = useState(props.campaignId ?? "");

	const variantsQuery = useCreativeAbVariants(campaignId.trim());
	const reportQuery = useCreativeAbReport(campaignId.trim());
	const replaceMut = useCreativeAbReplaceVariants(campaignId.trim());
	const assignMut = useCreativeAbAssign(campaignId.trim());
	const convertMut = useCreativeAbConvert(campaignId.trim());

	// Editable draft is seeded from the server variant set whenever it changes
	// under the hood, but the user can mutate freely. We keep it simple: a
	// fresh form on each campaign change, and a "reset" button to resync.
	const [draft, setDraft] = useState<VariantInput[]>([]);
	const [draftKey, setDraftKey] = useState<string>("");
	const [clickId, setClickId] = useState("");
	const [lastAssign, setLastAssign] = useState<AssignResponse | null>(null);

	const serverVariants = variantsQuery.data?.variants ?? [];

	// Seed the draft with server rows the first time they arrive for a given
	// campaign id. After that, the user is in charge.
	if (draftKey !== campaignId && serverVariants.length > 0) {
 setDraft(
 serverVariants.map((v) => ({
 variant_key: v.variant_key,
 asset_kind: v.asset_kind,
 asset_ref: v.asset_ref,
 split_bps: v.split_bps,
 })),
 );
 setDraftKey(campaignId);
	}

	const draftSum = useMemo(
 () => draft.reduce((acc, v) => acc + (Number(v.split_bps) || 0), 0),
 [draft],
	);
	const draftValid =
 draft.length > 0 &&
 draftSum === TOTAL_BPS &&
 draft.every(
 (v) =>
 v.variant_key.trim().length > 0 &&
 v.asset_ref.trim().length > 0 &&
 Number.isInteger(v.split_bps) &&
 v.split_bps >= 0 &&
 v.split_bps <= TOTAL_BPS,
 );

	const handleCampaignSubmit = (e: FormEvent) => {
 e.preventDefault();
 setDraftKey(""); // allow reseed when variants resolve
 setLastAssign(null);
	};

	const handleAddRow = () => {
 setDraft((d) => [...d, emptyDraft()]);
	};

	const handleRemoveRow = (idx: number) => {
 setDraft((d) => d.filter((_, i) => i !== idx));
	};

	const handleDraftChange = (
 idx: number,
 patch: Partial<VariantInput>,
	) => {
 setDraft((d) =>
 d.map((row, i) => (i === idx ? { ...row, ...patch } : row)),
 );
	};

	const handleReseed = () => {
 setDraft(
 serverVariants.map((v) => ({
 variant_key: v.variant_key,
 asset_kind: v.asset_kind,
 asset_ref: v.asset_ref,
 split_bps: v.split_bps,
 })),
 );
	};

	const handleSave = (e: FormEvent) => {
 e.preventDefault();
 if (!draftValid) return;
 replaceMut.mutate(draft);
	};

	const handleAssign = (e: FormEvent) => {
 e.preventDefault();
 const cid = clickId.trim();
 if (!cid) return;
 assignMut.mutate(cid, {
 onSuccess: (data) => setLastAssign(data),
 });
	};

	const handleConvert = () => {
 const cid = clickId.trim();
 if (!cid) return;
 convertMut.mutate(cid);
	};

	const reportRows = reportQuery.data?.variants ?? [];
	const totalViews = reportRows.reduce((acc, r) => acc + r.views, 0);
	const totalConversions = reportRows.reduce(
 (acc, r) => acc + r.conversions,
 0,
	);
	const overallCrBps =
 totalViews > 0
 ? Math.round((totalConversions / totalViews) * TOTAL_BPS)
 : 0;

	return (
 <main
 data-testid="creative-ab-page"
 className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
 >
 <header className="flex flex-col gap-2">
 <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
 creative-ab
 </p>
 <h1 className="text-3xl font-semibold text-green-700">
 Creative A/B Testing
 </h1>
 <p className={HINT}>
 Define weighted variants per campaign. Each click_id is bucketed
 deterministically; conversions roll up per variant.
 </p>
 </header>

 <section className={CARD} aria-label="Campaign selector">
 <form
 onSubmit={handleCampaignSubmit}
 className="flex flex-col gap-3 sm:flex-row sm:items-end"
 data-testid="creative-ab-campaign-form"
 >
 <div className="flex-1">
 <label className={LABEL} htmlFor="campaign-id-input">
 Campaign ID
 </label>
 <input
 id="campaign-id-input"
 type="text"
 className={INPUT}
 placeholder="e.g. camp_spring"
 value={campaignId}
 onChange={(e) => setCampaignId(e.currentTarget.value)}
 data-testid="creative-ab-campaign-input"
 />
 </div>
 <button
 type="submit"
 className={BTN_PRIMARY}
 data-testid="creative-ab-load"
 >
 Load
 </button>
 </form>
 </section>

 {campaignId.trim() ? (
 <>
 <section className={CARD} aria-label="Variants editor">
 <div className="flex items-start justify-between gap-3">
 <div>
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">
 Variants
 </h2>
 <p className={HINT}>
 Sum of split_bps must equal {TOTAL_BPS} (100%).
 </p>
 </div>
 <div className="flex items-center gap-2">
 <button
 type="button"
 className={BTN_GHOST}
 onClick={handleReseed}
 disabled={serverVariants.length === 0}
 data-testid="creative-ab-reseed"
 >
 Reset to saved
 </button>
 <button
 type="button"
 className={BTN_GHOST}
 onClick={handleAddRow}
 data-testid="creative-ab-add-row"
 >
 + Add row
 </button>
 </div>
 </div>

 {variantsQuery.isLoading ? (
 <p
 className="mt-3 text-sm text-zinc-500"
 data-testid="creative-ab-variants-loading"
 >
 Loading variants.
 </p>
 ) : variantsQuery.error ? (
 <p
 className={`mt-3 ${ERROR_TEXT}`}
 role="alert"
 data-testid="creative-ab-variants-error"
 >
 {variantsQuery.error.message}
 </p>
 ) : draft.length === 0 ? (
 <p
 className="mt-3 text-sm text-zinc-500"
 data-testid="creative-ab-variants-empty"
 >
 No variants yet. Add a row to get started.
 </p>
 ) : (
 <form
 onSubmit={handleSave}
 className="mt-3 flex flex-col gap-3"
 data-testid="creative-ab-variants-form"
 >
 <div className="overflow-x-auto">
 <table className="w-full border-separate border-spacing-0 text-sm">
 <thead>
 <tr className="text-left text-xs uppercase tracking-wide text-zinc-500">
 <th className="pb-2 pr-3 font-medium">Key</th>
 <th className="pb-2 pr-3 font-medium">Kind</th>
 <th className="pb-2 pr-3 font-medium">Asset ref</th>
 <th className="pb-2 pr-3 font-medium">Split (bps)</th>
 <th className="pb-2 font-medium" />
 </tr>
 </thead>
 <tbody>
 {draft.map((row, idx) => (
 <tr
 key={idx}
 className="align-top"
 data-testid={`creative-ab-draft-row-${idx}`}
 >
 <td className="py-1 pr-3">
 <input
 type="text"
 className={INPUT}
 placeholder="control"
 value={row.variant_key}
 onChange={(e) =>
 handleDraftChange(idx, {
 variant_key: e.currentTarget.value,
 })
 }
 data-testid={`creative-ab-key-input-${idx}`}
 />
 </td>
 <td className="py-1 pr-3">
 <select
 className={SELECT}
 value={row.asset_kind}
 onChange={(e) =>
 handleDraftChange(idx, {
 asset_kind: e.currentTarget
 .value as AssetKind,
 })
 }
 data-testid={`creative-ab-kind-select-${idx}`}
 >
 {ASSET_KINDS.map((k) => (
 <option key={k} value={k}>
 {k}
 </option>
 ))}
 </select>
 </td>
 <td className="py-1 pr-3">
 <input
 type="text"
 className={INPUT}
 placeholder="creative_123 or https://lp"
 value={row.asset_ref}
 onChange={(e) =>
 handleDraftChange(idx, {
 asset_ref: e.currentTarget.value,
 })
 }
 data-testid={`creative-ab-ref-input-${idx}`}
 />
 </td>
 <td className="py-1 pr-3">
 <input
 type="number"
 min={0}
 max={TOTAL_BPS}
 step={1}
 className={INPUT}
 value={row.split_bps}
 onChange={(e) => {
 const n = Number.parseInt(
 e.currentTarget.value,
 10,
 );
 handleDraftChange(idx, {
 split_bps: Number.isFinite(n) ? n : 0,
 });
 }}
 data-testid={`creative-ab-bps-input-${idx}`}
 />
 </td>
 <td className="py-1 text-right">
 <button
 type="button"
 className={BTN_DANGER}
 onClick={() => handleRemoveRow(idx)}
 data-testid={`creative-ab-remove-row-${idx}`}
 >
 Remove
 </button>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>

 <div className="flex flex-wrap items-center justify-between gap-3">
 <p
 className={`text-xs font-medium ${
 draftSum === TOTAL_BPS
 ? "text-green-700"
 : "text-red-600"
 }`}
 data-testid="creative-ab-draft-sum"
 >
 Sum: {draftSum} / {TOTAL_BPS} bps
 </p>
 <button
 type="submit"
 className={BTN_PRIMARY}
 disabled={!draftValid || replaceMut.isPending}
 data-testid="creative-ab-save-variants"
 >
 {replaceMut.isPending ? "Saving." : "Save variants"}
 </button>
 </div>
 {replaceMut.error ? (
 <p
 className={ERROR_TEXT}
 role="alert"
 data-testid="creative-ab-save-error"
 >
 {replaceMut.error.message}
 </p>
 ) : null}
 </form>
 )}
 </section>

 <section className={CARD} aria-label="Assign + Convert tester">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">
 Test assignment
 </h2>
 <p className={HINT}>
 Same click_id always returns the same variant; Convert flips the
 view to converted.
 </p>
 <form
 onSubmit={handleAssign}
 className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end"
 data-testid="creative-ab-assign-form"
 >
 <div className="flex-1">
 <label className={LABEL} htmlFor="click-id-input">
 click_id
 </label>
 <input
 id="click-id-input"
 type="text"
 className={INPUT}
 placeholder="e.g. clk_abc123"
 value={clickId}
 onChange={(e) => setClickId(e.currentTarget.value)}
 data-testid="creative-ab-click-input"
 />
 </div>
 <button
 type="submit"
 className={BTN_PRIMARY}
 disabled={
 assignMut.isPending || clickId.trim().length === 0
 }
 data-testid="creative-ab-assign-btn"
 >
 {assignMut.isPending ? "Assigning." : "Assign"}
 </button>
 <button
 type="button"
 className={BTN_GHOST}
 onClick={handleConvert}
 disabled={
 convertMut.isPending || clickId.trim().length === 0
 }
 data-testid="creative-ab-convert-btn"
 >
 {convertMut.isPending ? "Converting." : "Convert"}
 </button>
 </form>
 {assignMut.error ? (
 <p
 className={`mt-2 ${ERROR_TEXT}`}
 role="alert"
 data-testid="creative-ab-assign-error"
 >
 {assignMut.error.message}
 </p>
 ) : null}
 {convertMut.error ? (
 <p
 className={`mt-2 ${ERROR_TEXT}`}
 role="alert"
 data-testid="creative-ab-convert-error"
 >
 {convertMut.error.message}
 </p>
 ) : null}
 {lastAssign ? (
 <div
 className="mt-3 rounded-[6px] border border-green-200 bg-green-50 p-3 text-sm"
 data-testid="creative-ab-last-assign"
 >
 <p className="font-medium text-green-800">
 {lastAssign.variant_key}
 <span className="ml-2 text-xs font-normal text-green-700">
 ({lastAssign.asset_kind})
 </span>
 </p>
 <p className="font-mono text-xs text-green-900/80">
 {lastAssign.asset_ref}
 </p>
 </div>
 ) : null}
 </section>

 <section className={CARD} aria-label="Conversion report">
 <h2 className="text-green-700 font-semibold text-base font-semibold text-zinc-900">
 Report
 </h2>
 {reportQuery.isLoading ? (
 <p
 className="mt-3 text-sm text-zinc-500"
 data-testid="creative-ab-report-loading"
 >
 Loading report.
 </p>
 ) : reportQuery.error ? (
 <p
 className={`mt-3 ${ERROR_TEXT}`}
 role="alert"
 data-testid="creative-ab-report-error"
 >
 {reportQuery.error.message}
 </p>
 ) : reportRows.length === 0 ? (
 <p
 className="mt-3 text-sm text-zinc-500"
 data-testid="creative-ab-report-empty"
 >
 No report rows yet. Save variants and assign a click_id.
 </p>
 ) : (
 <div className="mt-3 overflow-x-auto">
 <table
 className="w-full border-separate border-spacing-0 text-sm"
 data-testid="creative-ab-report-table"
 >
 <thead>
 <tr className="text-left text-xs uppercase tracking-wide text-zinc-500">
 <th className="border-b border-zinc-200 pb-2 pr-3 font-medium">
 Variant
 </th>
 <th className="border-b border-zinc-200 pb-2 pr-3 text-right font-medium">
 Views
 </th>
 <th className="border-b border-zinc-200 pb-2 pr-3 text-right font-medium">
 Conversions
 </th>
 <th className="border-b border-zinc-200 pb-2 text-right font-medium">
 CR
 </th>
 </tr>
 </thead>
 <tbody>
 {reportRows.map((r) => (
 <tr
 key={r.variant_key}
 data-testid={`creative-ab-report-row-${r.variant_key}`}
 >
 <td className="py-2 pr-3 font-medium text-zinc-900">
 {r.variant_key}
 </td>
 <td className="py-2 pr-3 text-right font-mono text-zinc-700">
 {r.views}
 </td>
 <td className="py-2 pr-3 text-right font-mono text-zinc-700">
 {r.conversions}
 </td>
 <td className="py-2 text-right font-mono text-zinc-700">
 {fmtBpsAsPct(r.cr_bps)}
 </td>
 </tr>
 ))}
 <tr className="bg-zinc-50">
 <td className="py-2 pr-3 font-semibold text-zinc-900">
 Total
 </td>
 <td className="py-2 pr-3 text-right font-mono font-semibold text-zinc-900">
 {totalViews}
 </td>
 <td className="py-2 pr-3 text-right font-mono font-semibold text-zinc-900">
 {totalConversions}
 </td>
 <td className="py-2 text-right font-mono font-semibold text-green-700">
 {fmtBpsAsPct(overallCrBps)}
 </td>
 </tr>
 </tbody>
 </table>
 </div>
 )}
 </section>
 </>
 ) : (
 <section
 className={CARD}
 data-testid="creative-ab-prompt"
 aria-label="Pick a campaign"
 >
 <p className="text-sm text-zinc-500">
 Enter a campaign ID above to manage its A/B variants.
 </p>
 </section>
 )}
 </main>
	);
}

export default CreativeAbPage;
